<template>
  <v-menu
    ref="menuRef"
    v-model="menu"
    :close-on-content-click="false"
    :nudge-right="40"
    transition="scale-transition"
    offset-y
    min-width="290px"
    @input="updateValue"
  >
    <template #activator="{ on }">
      <v-text-field
        :class="`${contentClass}`"
        :value="formattedDate"
        :dense="dense"
        :hide-details="hideDetails"
        :error-messages="errorMessages"
        :outlined="outlined"
        :disabled="disabled"
        :label="label"
        :clearable="clearable"
        readonly
        v-bind="$attrs"
        :retain-focus-on-click="false"
        @click:clear="clear"
        v-on="on"
        @click:append-outer.prevent="$emit('click:append-outer', $event)"
      />
    </template>
    <div class="d-flex flex-column flex-sm-row">
      <v-date-picker
        v-model="date"
        class="date-picker-menu"
        :type="type"
        no-title
        :min="dateStringFromDate8601(minDate)"
        :max="dateStringFromDate8601(maxDate)"
        :first-day-of-week="$localized.firstDayOfWeek()"
        color="brand"
        scrollable
        @input="updateValue()"
      />
      <v-time-picker
        ref="timePickerRef"
        v-model="time"
        :format="$localized.getTimeFormat()"
        color="brand"
        ampm-in-title
        :min="props.minDate && dDate && isDateToday(dDate) ? timeStringFromDate(props.minDate) : undefined"
        :max="props.maxDate && dDate && isDateSameIgnoringTime(dDate, props.maxDate) ? timeStringFromDate(props.maxDate) : undefined"
        :allowed-minutes="allowedMinutes"
        @click:minute="menuRef?.save(time); updateValue(); menu = false"
      />
    </div>
  </v-menu>
</template>

<script setup lang="ts">
import Vue, {
  computed, PropType, ref, watch,
} from 'vue';
import {
  dateStringFromDate8601, timeStringFromDate, dateFromDateTimeString8601, isDateToday,
  isDateSameIgnoringTime,
} from '@/services/time-utils';
import { VDatePicker, VMenu } from 'vuetify/lib';
import { TranslateResult } from 'vue-i18n';

const props = defineProps({
  value: { type: Object as PropType<Date | null>, required: true },
  formatFn: { type: Function as PropType<((d: Date) => string | null)>, required: false, default: undefined },
  disabled: { type: Boolean, required: false, default: false },
  label: { type: [String, Object] as PropType<string|TranslateResult>, required: false, default: '' },
  type: { type: String, required: false, default: 'date' },
  dense: { type: Boolean, required: false, default: false },
  hideDetails: { type: Boolean, required: false, default: false },
  outlined: { type: Boolean, required: false, default: false },
  clearable: { type: Boolean, required: false, default: false },
  errorMessages: { type: Array, required: false, default: null },
  contentClass: { type: String, required: false, default: '' },
  minDate: { type: Object as PropType<Date|undefined>, required: false, default: undefined },
  maxDate: { type: Object as PropType<Date|undefined>, required: false, default: undefined },
  allowedMinutes: { type: Array, required: false, default: () => [0, 15, 30, 45] as Array<Number> },
  defaultTime: { type: String, required: false, default: undefined },
});

const emit = defineEmits<{(e: 'input', value: Date | null): void, (e: 'click:append-outer', evt: any | null): void}>();
const date = ref(dateStringFromDate8601(props.value ?? undefined));
const time = ref(timeStringFromDate(props.value));
const menu = ref(false);

const menuRef = ref(undefined as undefined | Vue & {save: (val: any) => void});
const timePickerRef = ref(undefined as undefined | Vue & {selectingHour: boolean});

const dateTimeStr = computed(() => (`${date.value} ${time.value ?? props.defaultTime ?? ''}`));

const formattedDate = computed(
  () => (!props.formatFn ? dateTimeStr.value : props.formatFn(dateFromDateTimeString8601(dateTimeStr.value)!)),
);

const dDate = computed(() => dateFromDateTimeString8601(dateTimeStr.value));

watch(() => props.value, () => {
  date.value = dateStringFromDate8601(props.value ?? undefined)!;
  time.value = timeStringFromDate(props.value)!;
});

const updateValue = () => {
  if (timePickerRef.value) { timePickerRef.value.selectingHour = true; }
  emit('input', dateFromDateTimeString8601(dateTimeStr.value));
};

const clear = () => {
  date.value = null;
  time.value = null;
  updateValue();
};

</script>
